import DesignTokens from '@tableau/design-tokens';
import { ArrowTriangleDownIconRaw } from '@tableau/icons/ArrowTriangleDownIcon/raw';
import { TabStyles } from '@tableau/widgets';
import { CSSProperties } from 'react';
import { cssRule } from 'typestyle';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

/*
 * We need normal CSS to override TinyMCE's styling
 */
const defaultFont: CSSProperties = {
  fontFamily: `${CommonStyles.Typography.FontFamily} !important`,
  fontSize: TabStyles.Typography.Sizing.Default,
};

// Note: Using !important here so :focus overrides the :hover style.
// It's possible that typestyle doesn't keep the order of the CSS,
// so this is a failsafe. !important is a one-off here.
const focusStyle = {
  border: `1px solid ${applyColor(DesignTokens.color.utility.focus.default.default, CommonStyles.Colors.FocusBorderBlue)} !important`,
  'box-shadow': `0 0 4px 0 ${applyColor(DesignTokens.color.utility.focus.default.default, CommonStyles.Colors.FocusBorderBlue)} !important`
};

/**
 * This is not ideal to duplicate button styles, but we want to join
 * forces with the widgets team on finding a long-term solution.
 */
const baseButtonStyle = {
  ...defaultFont,
  height: '24px',
  backgroundColor: 'transparent',
  display: 'inline-block',
  border: '1px solid '+applyColor(DesignTokens.color.elevation.border.inverse.subtle, '#666'),
  borderRadius: 2,
  lineHeight: '22px',
  padding: '0 24px',
  color: applyColor(DesignTokens.color.font.default.secondary, '#666'),
  outline: 'none'
};

const buttonStyle = {
  primary: {
    ...baseButtonStyle,
    backgroundColor: applyColor(DesignTokens.color.link.inline.default.default, '#1a699e'),
    borderColor: applyColor(DesignTokens.color.link.inline.default.default, '#1a699e'),
    color: applyColor(DesignTokens.color.font.inverse.default, '#fff'),
    $nest: {
      '&:hover': {
        backgroundColor: applyColor(DesignTokens.color.link.inline.default.hover, '#2a79af'),
        borderColor: applyColor(DesignTokens.color.link.inline.default.hover, '#2a79af')
      },
      '&:active': {
        backgroundColor: applyColor(DesignTokens.color.link.inline.default.active, '#15557f'),
        borderColor: applyColor(DesignTokens.color.link.inline.default.active, '#15557f')
      },
      '&:focus': {
        ...focusStyle
      }
    }
  },
  outline: {
    ...baseButtonStyle,
    $nest: {
      '&:hover': {
        backgroundColor: applyColor(DesignTokens.color.component.background.neutral.default.hover, '#ebebeb')
      },
      '&:active': {
        backgroundcolor: applyColor(DesignTokens.color.elevation.surface.default.subtle, '#ccc'),
        borderColor: applyColor(DesignTokens.color.component.border.neutral.default.default, '#333'),
        color: applyColor(DesignTokens.color.font.default.default, '#333'),
      },
      '&:focus': {
        ...focusStyle
      }
    }
  }
};

/**
 * Tooltip popup
 */

// Proper container height for the popup
cssRule('.tox-pop', {
  maxHeight: '50px !important'  /* TinyMCE applies max-height via style attribute */
});

// Popup textfield
cssRule('.tox-pop .tox-toolbar-textfield', {
  ...defaultFont
});

/**
 * Right click menu popup
 */

cssRule('.tox-menu .tox-collection__item-label', {
  ...defaultFont,
});

/**
 * Dialog Styling
 */

cssRule('.tox-dialog .tox-dialog__body-content', {
  padding: '12px 18px'
});

cssRule('.tox-dialog .tox-dialog__header', {
  backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, TabStyles.Colors.F2),
  height: '30px',
  padding: '0 18px'
});

cssRule('.tox-dialog .tox-dialog__title', {
  color: applyColor(DesignTokens.color.component.content.neutral.default.default, TabStyles.Colors.Black),
  ...defaultFont,
  lineHeight: 'inherit'
});

cssRule('.tox-dialog .tox-label', {
  ...defaultFont,
  lineHeight: '19px'
});

cssRule('.tox-dialog .tox-textfield', {
  ...generateInputFieldStyle(),
  marginBottom: '12px'
});

cssRule('.tox-dialog .tox-selectfield select', {
  ...generateInputFieldStyle()
});

cssRule('.tox-dialog .tox-selectfield__icon-js svg', {
  height: 18,
  width: 18,
  backgroundImage: `url(${ArrowTriangleDownIconRaw(18, CommonStyles.Colors.Grey102)})`,
  $nest: {
    'path': {
      display: 'none'
    }
  }
});

// Intentionally hide TinyMCE's "Open Link In..." dropdown
// since we don't respect it when converting to markdown.
cssRule('.tox-dialog .tox-form .tox-form__group:nth-of-type(4)', {
  display: 'none'
});

function generateInputFieldStyle() {
  return {
    border: '1px solid transparent',
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L6)}`,
    borderRadius: '1px 1px 0 0',
    ...defaultFont,
    lineHeight: 'inherit',
    padding: '0px 7px',
    height: '24px',
    $nest: {
      '&:hover': {
        borderBottomColor: applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L8),
        backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, '#FAFAFA')
      },
      '&:focus': {
        backgroundColor: 'transparent'+' !important',
        ...focusStyle
      }
    }
  };
}

// Close button
cssRule('.tox-dialog .tox-button--naked', {
  display: 'none !important',
});

// Ok button
cssRule('.tox .tox-dialog .tox-button', {
  ...buttonStyle.primary
});

// Cancel button
cssRule('.tox .tox-dialog .tox-button.tox-button--secondary', {
  ...buttonStyle.outline
});

cssRule('.tox .tox-dialog__footer', {
  border: 'none',
  padding: '0 18px',
  paddingBottom: '12px'
});

cssRule('.mce-content-body [data-mce-selected=inline-boundary]', {
  backgroundColor: 'none'
});

